import { useMemo, useState } from "react";
import { Link2, EyeOff, Undo2, Landmark } from "lucide-react";
import { DataTable, type Column } from "../ui/DataTable";
import { Badge } from "../ui/Badge";
import { RowActions } from "../ui/RowActions";
import { eur, fmtDate } from "../../lib/format";
import type { BankTransaction, BankMatchStatus } from "../../types/bank";
import { matchStatusLabels } from "../../types/bank";

interface TransactionsTableProps {
  rows: BankTransaction[];
  selected: BankTransaction[];
  onSelectionChange: (rows: BankTransaction[]) => void;
  onVisibleRowsChange: (rows: BankTransaction[]) => void;
  onMatch: (tx: BankTransaction) => void;
  onUnmatch: (tx: BankTransaction) => void;
  onIgnore: (tx: BankTransaction) => void;
  onUnignore: (tx: BankTransaction) => void;
}

const statusBadge: Record<BankMatchStatus, string> = {
  UNMATCHED: "bg-slate-100 text-slate-600 border-slate-200",
  SUGGESTED: "bg-amber-50 text-amber-700 border-amber-200",
  MATCHED: "bg-brand-50 text-brand-700 border-brand-200",
  IGNORED: "bg-slate-50 text-slate-400 border-slate-200",
};

/** Tabel met alle banktransacties + status-badge en match-acties. */
export function TransactionsTable({
  rows,
  selected,
  onSelectionChange,
  onVisibleRowsChange,
  onMatch,
  onUnmatch,
  onIgnore,
  onUnignore,
}: TransactionsTableProps) {
  const [showIgnored, setShowIgnored] = useState(false);
  const visible = useMemo(() => (showIgnored ? rows : rows.filter((r) => r.matchStatus !== "IGNORED")), [rows, showIgnored]);

  const columns: Column<BankTransaction>[] = [
    {
      key: "date",
      header: "Datum",
      accessor: (t) => fmtDate(t.date),
      sortValue: (t) => new Date(t.date).getTime(),
    },
    { key: "counterparty", header: "Tegenpartij", accessor: (t) => t.counterparty || "—" },
    { key: "description", header: "Omschrijving", accessor: (t) => t.description || "—" },
    {
      key: "amountEur",
      header: "Bedrag",
      accessor: (t) => eur(Number(t.amountEur)),
      className: "text-right",
      sortValue: (t) => Number(t.amountEur),
      render: (t) => {
        const n = Number(t.amountEur);
        return (
          <span className={`text-right font-semibold ${n >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {eur(n)}
          </span>
        );
      },
    },
    {
      key: "matchStatus",
      header: "Status",
      accessor: (t) => matchStatusLabels[t.matchStatus],
      render: (t) => (
        <Badge className={statusBadge[t.matchStatus]}>
          {matchStatusLabels[t.matchStatus]}
          {t.matchStatus === "SUGGESTED" && t.matchConfidence != null ? ` (${Math.round(t.matchConfidence)}%)` : ""}
        </Badge>
      ),
      sortValue: (t) => t.matchStatus,
    },
    {
      key: "link",
      header: "Koppeling",
      accessor: (t) => t.invoice?.number || t.expense?.description || "—",
      sortable: false,
      render: (t) => {
        if (t.invoice) return <span className="text-slate-700">Factuur {t.invoice.number}{t.invoice.contact?.companyName ? ` · ${t.invoice.contact.companyName}` : ""}</span>;
        if (t.expense) return <span className="text-slate-600">{t.expense.description}</span>;
        if (t.matchRule === "BOOK_INTERNAL") return <span className="text-indigo-600 truncate max-w-[16rem] inline-block" title={t.matchNote ?? ""}>📋 {t.matchNote}</span>;
        if (t.matchStatus === "SUGGESTED" && t.matchNote) return <span className="text-amber-600 truncate max-w-[16rem] inline-block" title={t.matchNote}>{t.matchNote}</span>;
        return <span className="text-slate-300">—</span>;
      },
    },
    {
      key: "actions",
      header: "Acties",
      accessor: () => "",
      sortable: false,
      width: "1%",
      render: (t) => (
        <div className="flex items-center gap-1">
          {t.matchStatus !== "IGNORED" && (
            <button
              onClick={() => onMatch(t)}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600"
              title="Koppelen / suggesties"
            >
              <Link2 size={20} />
            </button>
          )}
          <RowActions
            items={[
              { label: "Ontkoppelen", icon: Undo2, onClick: () => onUnmatch(t), hidden: t.matchStatus !== "MATCHED" },
              { label: "Genegeerd maken", icon: EyeOff, onClick: () => onIgnore(t), hidden: t.matchStatus === "MATCHED" || t.matchStatus === "IGNORED" },
              { label: "Niet meer negeren", icon: Undo2, onClick: () => onUnignore(t), hidden: t.matchStatus !== "IGNORED" },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <label className="inline-flex items-center gap-2 text-sm text-slate-600 font-medium cursor-pointer select-none">
        <input type="checkbox" checked={showIgnored} onChange={(e) => setShowIgnored(e.target.checked)} className="rounded border-slate-300" />
        Genegeerde transacties tonen
      </label>
      {visible.length === 0 ? (
        <div className="surface-card p-10 flex flex-col items-center gap-3 text-center">
          <Landmark size={40} className="text-slate-300" />
          <p className="text-slate-500 font-medium">Nog geen banktransacties.</p>
          <p className="text-sm text-slate-400">Importeer een bankafschrift (CSV of PDF) via het tabblad "Importeren".</p>
        </div>
      ) : (
        <DataTable
          rows={visible}
          columns={columns}
          rowKey={(t) => t.id}
          searchPlaceholder="Zoek transacties..."
          storageKey="bank-transacties"
          initialSort={{ key: "date", dir: "desc" }}
          groupBy={{ getDate: (t) => t.date, getAmount: (t) => Number(t.amountEur) }}
          onVisibleRowsChange={onVisibleRowsChange}
          selectable
          onSelectionChange={onSelectionChange}
        />
      )}
      {/* selected prop alleen voor export-keuze in de pagina (geen UI nodig hier). */}
      <span className="hidden">{selected.length}</span>
    </div>
  );
}